import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import moment, { duration } from 'moment'

import { ServerModel } from 'app/models/serverModel'
import { ConnectionStatus } from 'app/constants/connectionStatus'
import { getConnectionIcon } from 'app/common/converters/connectionStatusToIconConverter'
import { getConnectionStatusText } from 'app/common/converters/connectionStatusToTextConverter'
import { formatExtendedDuration } from 'app/common/dateHelper'
import { PRECISE_TIME_FORMAT } from 'app/constants/date'
import {
  addServerRequest,
  deleteServersRequest,
  editServerRequest,
  fetchServers,
  mapServerFields,
  serversKey,
} from 'app/containers/administration/serverQueries'
import { isAdmin } from 'app/services/local/roleService'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import { Button } from '@/ui/button'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { toast } from '@/lib/toasts/api'
import { EditServerForm } from './components/EditServerForm'
import ServerUsersModal from 'app/containers/administration/serverUsersModalContainer'
import { AdminModal } from '../shared/AdminModal'
import { AdminColumn, AdminTable, AdminToolbar } from '../shared/AdminTable'

type Props = {
  currentCompanyId?: string
  onGoToDatabases: (server: ServerModel) => void
  onGoToCompanies: (server: ServerModel) => void
}

const REFRESH_MS = 5000

/**
 * Zakładka SERWERY panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/servers.tsx`. Lista odświeża się co 5 s,
 * tak jak dotąd; sortowanie i filtrowanie po stronie klienta, bo endpoint oddaje całość.
 */
const AdminServersPageContent: React.FC<Props> = ({
  currentCompanyId,
  onGoToDatabases,
  onGoToCompanies,
}) => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const { selectedGroup, resetAll } = useTable()

  const [search, setSearch] = React.useState('')
  const [editedServer, setEditedServer] = React.useState<ServerModel | null>(
    null,
  )
  const [usersServer, setUsersServer] = React.useState<ServerModel | null>(null)
  const [form, setForm] = React.useState({
    name: '',
    address: '',
    login: '',
    password: '',
    isPrivate: false,
  })

  const addServer = async () => {
    try {
      const response = await addServerRequest({
        ...form,
        companyId: currentCompanyId,
      } as any)
      const added = mapServerFields((response as any).content?.data ?? response)
      if (!added?.id) throw new Error('no id')
      setForm({
        name: '',
        address: '',
        login: '',
        password: '',
        isPrivate: false,
      })
      await queryClient.invalidateQueries({
        queryKey: serversKey(currentCompanyId),
      })
      toast.success({
        title: t('administrationPanel-servers.server-added'),
        text: form.name,
      })
    } catch {
      toast.error({
        title: t('administrationPanel-servers.server-not-added'),
        text: form.name,
      })
    }
  }

  const makeDefault = async (server: ServerModel) => {
    try {
      await editServerRequest({ ...server, isDefault: true })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'servers'] })
      toast.success({
        title: t('administrationPanel-servers.default-server-set'),
        text: server.name,
      })
    } catch {
      toast.error({
        title: t('administrationPanel-servers.server-not-edited'),
        text: server.name,
      })
    }
  }

  const query = useQuery({
    queryKey: serversKey(currentCompanyId),
    queryFn: () => fetchServers(currentCompanyId ?? null),
    refetchInterval: REFRESH_MS,
  })

  const servers: ServerModel[] = React.useMemo(() => {
    const all = query.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (server) =>
        server.name?.toLowerCase().includes(needle) ||
        server.address?.toLowerCase().includes(needle),
    )
  }, [query.data, search])

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])
  React.useEffect(() => () => clearAdminSelection(), [])

  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        const ids = selectedRef.current.map(String)
        if (ids.length === 0) return
        const result = await deleteServersRequest(ids)
        const failed = result.filter((item: any) => !item.success)
        if (failed.length > 0) {
          const names = (query.data ?? [])
            .filter((server) =>
              failed.some((item: any) => String(item.id) === String(server.id)),
            )
            .map((server) => server.name)
            .join(', ')
          toast.error({
            title: t('administrationPanel-servers.servers-deletion-failed'),
            text: names,
          })
        } else {
          toast.success({
            title: t('administrationPanel-servers.servers-deleted'),
          })
        }
        resetAll()
        clearAdminSelection()
        await queryClient.invalidateQueries({
          queryKey: serversKey(currentCompanyId),
        })
      }),
    [resetAll, queryClient, query.data, currentCompanyId, t],
  )

  const statusCell = (server: ServerModel) => {
    const statusText = t(getConnectionStatusText(server.status))
    const title =
      server.status === ConnectionStatus.NOT_CONNECTED &&
      server.lastErrorMessage != null
        ? `${statusText}: ${server.lastErrorMessage}`
        : statusText
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        <AdminIcon name={getConnectionIcon(server.status)} title={title} />
        {statusText}
      </span>
    )
  }

  const activityCell = (server: ServerModel) => {
    if (server.status === ConnectionStatus.CONNECTED) {
      return (
        <span className="tw-flex tw-justify-between tw-gap-3 tw-text-[#5EBF9A]">
          <span>{t('administrationPanel-servers.now')}</span>
          <span>
            {formatExtendedDuration(
              duration(moment().diff(moment(server.lastConnectedDate))),
              true,
            )}
          </span>
        </span>
      )
    }
    if (server.lastDisconnectedDate) {
      return (
        <span className="tw-flex tw-justify-between tw-gap-3 tw-text-[#F7001E]">
          <span>
            {moment(server.lastDisconnectedDate).format(PRECISE_TIME_FORMAT)}
          </span>
          <span>
            {formatExtendedDuration(
              duration(moment().diff(moment(server.lastDisconnectedDate))),
              true,
            )}
          </span>
        </span>
      )
    }
    return '?'
  }

  const columns: Array<AdminColumn<ServerModel>> = [
    { key: 'name', header: t('administrationPanel-servers.name'), width: 250 },
    {
      key: 'address',
      header: t('administrationPanel-servers.ip-address'),
      width: 260,
    },
    {
      key: 'status',
      header: t('administrationPanel-servers.status'),
      width: 185,
      render: statusCell,
    },
    {
      key: 'dbCount',
      header: t('administrationPanel-servers.db-count'),
      width: 130,
      render: (server) => (
        <button
          type="button"
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-primary hover:tw-underline"
          title={t('administrationPanel-servers.go-to-databases')}
          onClick={(event) => {
            event.stopPropagation()
            onGoToDatabases(server)
          }}
        >
          {server.databases}
        </button>
      ),
    },
    {
      key: 'companyCount',
      header: t('administrationPanel-servers.company-count'),
      width: 130,
      render: (server) => (
        <button
          type="button"
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-primary hover:tw-underline"
          title={t('administrationPanel-servers.go-to-companies')}
          onClick={(event) => {
            event.stopPropagation()
            onGoToCompanies(server)
          }}
        >
          {server.companies}
        </button>
      ),
    },
    {
      key: 'isPrivate',
      header: t('administrationPanel-servers.server-type'),
      width: 130,
      render: (server) => {
        const label = t(
          server.isPrivate
            ? 'administrationPanel-servers.private'
            : 'administrationPanel-servers.shared',
        )
        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1">
            <AdminIcon
              name={server.isPrivate ? 'lock' : 'unlock'}
              title={label}
            />
            {label}
          </span>
        )
      },
    },
    {
      key: 'isDefault',
      header: t('administrationPanel-servers.default-server'),
      width: 110,
      align: 'center',
      render: (server) => {
        if (server.isDefault)
          return (
            <AdminIcon
              name="default"
              className="tw-text-primary"
              title={t('administrationPanel-servers.default-server-hint')}
            />
          )
        if (server.isPrivate || server.inUse || !isAdmin()) return null
        return (
          <AdminIcon
            name="not-default"
            className="tw-cursor-pointer tw-text-content-secondary"
            title={t('administrationPanel-servers.set-default-server')}
            onClick={(event) => {
              event.stopPropagation()
              makeDefault(server)
            }}
          />
        )
      },
    },
    { key: 'createdBy', header: t('auditFields.created-by') },
    {
      key: 'createdDate',
      header: t('auditFields.created-date'),
      width: 160,
      render: (server) =>
        server.createdDate
          ? moment(server.createdDate).format(PRECISE_TIME_FORMAT)
          : '',
    },
    {
      key: 'lastDisconnectedDate',
      header: t('administrationPanel-servers.last-disconnected-date'),
      width: 240,
      render: activityCell,
    },
    {
      key: 'manageUsers',
      header: '',
      width: 48,
      align: 'center',
      render: (server) => (
        <AdminIcon
          name="people"
          title={t('administrationPanel-servers.users-section')}
          onClick={(event) => {
            event.stopPropagation()
            setUsersServer(server)
          }}
        />
      ),
    },
    {
      key: 'edit',
      header: '',
      width: 58,
      align: 'center',
      render: (server) => (
        <AdminIcon
          name="edit"
          title={t('operation.edit')}
          onClick={(event) => {
            event.stopPropagation()
            setEditedServer(server)
          }}
        />
      ),
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      {isAdmin() && (
        <div className="tw-flex tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
          {(['name', 'address', 'login'] as const).map((field) => (
            <input
              key={field}
              className="tw-w-[200px] tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-primary"
              placeholder={t(
                field === 'name'
                  ? 'administrationPanel-servers.name'
                  : field === 'address'
                    ? 'administrationPanel-servers.ip-address'
                    : 'administrationPanel-servers.login',
              )}
              value={form[field]}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, [field]: event.target.value }))
              }
            />
          ))}
          <input
            type="password"
            className="tw-w-[200px] tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-primary"
            placeholder={t('administrationPanel-servers.password')}
            value={form.password}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, password: event.target.value }))
            }
          />
          <CheckboxField
            id="server-private"
            label={t('administrationPanel-servers.private')}
            checked={form.isPrivate}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, isPrivate: event.target.checked }))
            }
          />
          <Button
            variant="primary"
            size="sm"
            disabled={!form.name || !form.address}
            onClick={addServer}
          >
            <AdminIcon name="add" className="white tw-h-4 tw-w-4" />
            {t('administrationPanel-servers.add-server')}
          </Button>
        </div>
      )}
      <AdminToolbar search={search} onSearch={setSearch} />
      <AdminTable
        idPrefix="server"
        rows={servers}
        columns={columns}
        rowId={(server) => server.id}
        selectable={isAdmin()}
        isLoading={query.isLoading}
      />

      {editedServer != null && (
        <AdminModal
          icon={null}
          hideFooter
          width={560}
          title={t('administrationPanel-servers.edit-server')}
          onClose={() => setEditedServer(null)}
        >
          <EditServerForm
            editedServer={editedServer}
            onClose={() => setEditedServer(null)}
          />
        </AdminModal>
      )}

      {usersServer != null && (
        <AdminModal
          icon={null}
          hideFooter
          width={560}
          title={t('administrationPanel-servers.users-section')}
          onClose={() => setUsersServer(null)}
        >
          <div style={{ minHeight: 200 }}>
            <ServerUsersModal
              server={usersServer}
              translate={t}
              onClose={() => setUsersServer(null)}
            />
          </div>
        </AdminModal>
      )}
    </div>
  )
}

export const AdminServersPage: React.FC<Props> = (props) => (
  <TableProvider>
    <AdminServersPageContent {...props} />
  </TableProvider>
)
